import {Dispatch} from "redux";

import {IStore} from "../../../app/reducers/hybrid_reducer";
import {apiLink} from "../../../app/routes/api_link";
import {IRouteState} from "../../../app/routes/data_fetcher/create_app_path_data_fetcher";
import {IServices} from "../../../app/services/IServices";
import {appendQueryString} from "../../../app/utils/append_query_string";
import {createRequestActionTypes} from "../../../app/utils/request_response_utils/factories/create_request_action_types";
import {getRequest} from "../../../app/utils/request_response_utils/request";
import {parseOfferListPath} from "../url_utils/parse_offer_list_path";
import {makeSlugToApiType} from "../../utils/make_slug_to_api_type";
import {ISearchResultRouteParams} from "./redirect_constraints";

export interface City {
    city: string;
    citySlug: string;
    offer_count: number;
}

export interface RegionData {
    region: string;
    cities: City[];
}
export interface RegionTopCityData {
    [region: string]: City[];
}

export const fetchCarListLocationsAtRouteType = createRequestActionTypes({name: "fetchCarListLocationsAtRoute", type: "GET", view: "cars_list"});
export const fetchCarListTopLocationsAtRouteType = createRequestActionTypes({name: "fetchCarTopListLocationsAtRoute", type: "GET", view: "cars_list"});

/** voivodeship-locations / top-locations ignore `model` — re-count via get-cars-search when model is set. */
const recountCityWithModel = async (
    services: Partial<IServices>,
    type: string | undefined,
    model: string,
    region: string,
    city: City,
    requestName: string
): Promise<City | null> => {
            const url = appendQueryString(apiLink.carDynamicSearch({})({}), {
        ...(type ? {type: makeSlugToApiType(type) || type} : {}),
        model,
        location: `${region}/${city.citySlug}`
    });
    try {
        const json = await getRequest(services, url, requestName);
        const count = typeof json?.count === "number" ? json.count : 0;
        if (count <= 0) {
            return null;
        }
        return {...city, offer_count: count};
    } catch {
        return null;
    }
};

const filterCitiesByModel = async (
    services: Partial<IServices>,
    type: string | undefined,
    model: string | undefined,
    region: string,
    cities: City[],
    requestPrefix: string
): Promise<City[]> => {
    if (!model || !cities.length) {
        return cities;
    }
    const recounted = await Promise.all(
        cities.map((city, index) => recountCityWithModel(services, type, model, region, city, `${requestPrefix}_${index}`))
    );
    return recounted.filter((city): city is City => Boolean(city));
};

export const fetchCarListLocationsAtRoute =
    (services: Partial<IServices>, route: IRouteState<ISearchResultRouteParams>) => async (dispatch: Dispatch, getState: () => IStore) => {
        const parsed = parseOfferListPath(route.params);
        const {location, type, model} = parsed;
        if (!location) {
            dispatch({type: fetchCarListTopLocationsAtRouteType.start});

            const url = appendQueryString(apiLink.topLocations({})(null), {
                ...(type ? {type: makeSlugToApiType(type) || type} : {})
            });
            return getRequest(services, url, "fetchCarTopLocations")
                .then(async (response: RegionTopCityData) => {
                    if (!response) {
                        return null;
                    }
                    let result = response;
                    if (model) {
                        const entries = await Promise.all(
                            Object.entries(response).map(async ([region, cities]) => {
                                const filtered = await filterCitiesByModel(
                                    services,
                                    type,
                                    model,
                                    region,
                                    cities || [],
                                    `topLoc_${region}`
                                );
                                return [region, filtered] as const;
                            })
                        );
                        result = Object.fromEntries(entries.filter(([, cities]) => cities.length > 0));
                    }
                    dispatch({type: fetchCarListTopLocationsAtRouteType.success, result});
                    return result;
                })
                .catch((err) => {
                    console.log("Error", err);
                    return null;
                });
        }

        dispatch({type: fetchCarListLocationsAtRouteType.start});

        const url = appendQueryString(apiLink.voivodeshipLocations({})(null), {
            wojew: location,
            ...(type ? {type: makeSlugToApiType(type) || type} : {})
        });
        return getRequest(services, url, "fetchCarListLocations")
            .then(async (response: RegionData) => {
                if (!response) {
                    return null;
                }
                const cities = await filterCitiesByModel(
                    services,
                    type,
                    model,
                    response.region || location,
                    response.cities || [],
                    "voiLoc"
                );
                const result: RegionData = {...response, cities};
                dispatch({type: fetchCarListLocationsAtRouteType.success, result});
                return result;
            })
            .catch((err) => {
                console.log("Error", err);
                return null;
            });
    };
